// client/src/components/AddProperty.js

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { MapPin, ChevronLeft, Loader2, Check } from "lucide-react";

const PROPERTY_TYPES = [
  { value: "",              label: "Select type..." },
  { value: "Detached",      label: "Detached House" },
  { value: "Semi-Detached", label: "Semi-Detached" },
  { value: "Townhouse",     label: "Townhouse" },
  { value: "Condo",         label: "Condo / Apartment" },
  { value: "Duplex",        label: "Duplex" },
  { value: "Other",         label: "Other" },
];

export default function AddProperty() {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [name, setName]             = useState("");
  const [address, setAddress]       = useState("");
  const [type, setType]             = useState("");
  const [sqft, setSqft]             = useState("");
  const [valuation, setValuation]   = useState("");
  const [imageUrl, setImageUrl]     = useState("");
  const [geocodingData, setGeocodingData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  // Refs for cleanup — prevents the recursive setTimeout memory leak
  const retryTimerRef     = useRef(null);
  const autocompleteRef   = useRef(null);

  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google?.maps?.places) {
        retryTimerRef.current = setTimeout(initAutocomplete, 250);
        return;
      }

      const input = document.getElementById("add-property-address-input");
      if (!input) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        fields: ["formatted_address", "geometry", "photos", "place_id"],
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) return;

        setAddress(place.formatted_address || "");
        setGeocodingData({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          placeId: place.place_id || "",
        });

        // getUrl() is the correct Maps JS API method — no manual URL construction,
        // no key exposed in the DOM.
        setImageUrl(place.photos?.[0]?.getUrl({ maxWidth: 1200 }) || "");
      });
    };

    initAutocomplete();

    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (address.trim().length < 5) {
      setError("Please enter a valid address.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const estimatedValue = valuation ? Number(valuation) : null;

      const payload = {
        name: name.trim() || address,
        address,
        type: type || null,
        sqft: sqft ? Number(sqft) : null,
        currentValue: estimatedValue,
        valuation: {
          estimate: estimatedValue,
          trend: "Stable",
          confidence: estimatedValue ? "Medium" : "Low",
          comps: [],
        },
        imageUrl,
        lat: geocodingData?.lat ?? null,
        lng: geocodingData?.lng ?? null,
        placeId: geocodingData?.placeId ?? null,
      };

      const token = getToken();
      const res = await fetch("/api/properties", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.fields) {
          const first = Object.values(err.fields)[0];
          throw new Error(first || err.error || "Failed to add property.");
        }
        throw new Error(err.error || err.message || "Failed to add property.");
      }

      navigate("/");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">

      {/* Header */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700 mb-6 transition"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-1">Add a property</h1>
      <p className="text-gray-500 text-sm mb-8">
        Enter the address and we'll pull in location data automatically.
      </p>

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property address <span style={{ color: '#CC0202' }}>*</span>
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              id="add-property-address-input"
              type="text"
              className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': '#CC0202' }}
              placeholder="123 Main Street, Toronto, ON"
              value={address}
              onChange={(e) => { setAddress(e.target.value); setGeocodingData(null); }}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              autoComplete="off"
              required
            />
          </div>
          {geocodingData ? (
            <p className="text-xs mt-1 flex items-center gap-1 text-green-600">
              <Check className="w-3 h-3" /> Location confirmed
            </p>
          ) : address ? (
            <p className="text-xs text-amber-500 mt-1">
              Select a suggestion from the dropdown to capture location
            </p>
          ) : null}
        </div>

        {/* Property name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property nickname
            <span className="ml-1 text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': '#CC0202' }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Main Home, Cottage, Investment"
          />
        </div>

        {/* Type + sqft row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
              <span className="ml-1 text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white"
              style={{ '--tw-ring-color': '#CC0202' }}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {PROPERTY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Square footage
              <span className="ml-1 text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': '#CC0202' }}
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
              placeholder="e.g. 2200"
            />
          </div>
        </div>

        {/* Estimated value */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estimated value
            <span className="ml-1 text-gray-400 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': '#CC0202' }}
              value={valuation}
              onChange={(e) => setValuation(e.target.value)}
              placeholder="1,250,000"
            />
          </div>
        </div>

        {/* Photo preview */}
        {imageUrl && (
          <div className="rounded-xl overflow-hidden border border-gray-100">
            <img src={imageUrl} alt="Property" className="w-full h-48 object-cover" />
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!address.trim() || submitting}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#CC0202' }}
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Add Property"
          )}
        </button>

      </form>
    </div>
  );
}
